import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getApiClient, handleApiError, makeCancelTokenSource, isErrorCancel } from '../api_client/get';
import { toast } from 'react-toastify';
import UserContext from './user';
import { useTokenTranslate } from './token_translation_context';

const PhoneNumbersContext = createContext();

const PAGE_LIMIT = 50;

export const PhoneNumbersProvider = ({ children }) => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loaded, setLoaded] = useState(false);
  // Phone numbers are only needed at checkout and on the profile/phone pages,
  // so nothing is fetched until a consumer calls ensurePhoneNumbersLoaded().
  const [wanted, setWanted] = useState(false);
  const { user } = useContext(UserContext);
  const { translate } = useTokenTranslate();

  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);

  const refreshCtsRef = useRef(null);

  useEffect(() => {
    return () => { refreshCtsRef.current?.cancel(); };
  }, []);

  const refreshPhoneNumbers = useCallback(async () => {
    refreshCtsRef.current?.cancel();
    const cts = makeCancelTokenSource();
    refreshCtsRef.current = cts;

    // Snapshot the user at fetch-start to detect session changes mid-pagination.
    const callerUser = userRef.current;
    try {
      const all = [];
      let paginationKey = null;
      do {
        const { data } = await getApiClient().getMyMobileNumbers(paginationKey, PAGE_LIMIT, cts);
        if (!data?.success) {
          if (userRef.current !== callerUser) return;
          setPhoneNumbers([]);
          if (data?.message) toast.error(data.message);
          return;
        }
        all.push(...(data.user_mobile_numbers ?? []));
        paginationKey = data.next_page_cursor || null;
      } while (paginationKey);
      if (userRef.current !== callerUser) return;
      setPhoneNumbers(all);
    } catch (error) {
      if (isErrorCancel(error)) return;
      if (userRef.current !== callerUser) return;
      setPhoneNumbers([]);
      handleApiError(error, (msg) => { if (msg) toast.error(msg); }, null, true);
    } finally {
      if (userRef.current === callerUser) setLoaded(true);
    }
  }, []);

  const ensurePhoneNumbersLoaded = useCallback(() => {
    setWanted(true);
  }, []);

  useEffect(() => {
    if (!user) {
      setPhoneNumbers([]);
      setLoaded(false);
      setWanted(false);
      return;
    }
    if (wanted) refreshPhoneNumbers();
  }, [user, wanted, refreshPhoneNumbers]);

  const addPhoneNumber = useCallback(async (mobileCountryCode, mobileNumber, mobileAddressCountry) => {
    let response;
    try {
      response = await getApiClient().addMobileNumber(mobileCountryCode, mobileNumber, mobileAddressCountry);
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message ||
        translate('web.phone_numbers_provider.failed_to_add_phone_number_error'));
    }
    const { data } = response;
    if (!data.success || !data.user_mobile_number) {
      throw new Error(data.message ?? translate('web.phone_numbers_provider.failed_to_add_phone_number_error'));
    }
    setPhoneNumbers(prev => [data.user_mobile_number, ...prev.filter(p => p.id !== data.user_mobile_number.id)]);
    return data.user_mobile_number;
  }, [translate]);

  const deletePhoneNumber = useCallback(async (id) => {
    let response;
    try {
      response = await getApiClient().deleteMobileNumber(id);
    } catch (err) {
      throw new Error(err.response?.data?.message || err.message ||
        translate('web.phone_numbers_provider.failed_to_delete_phone_number_error'));
    }
    const { data } = response;
    if (!data.success) {
      throw new Error(data.message ?? translate('web.phone_numbers_provider.failed_to_delete_phone_number_error'));
    }
    setPhoneNumbers(prev => prev.filter(p => p.id !== id));
  }, [translate]);

  const markPhoneNumberVerified = useCallback((id) => {
    setPhoneNumbers(prev =>
      prev.map(p => p.id === id ? { ...p, mobile_number_verified: true } : p)
    );
  }, []);

  return (
    <PhoneNumbersContext.Provider value={{
      phoneNumbers,
      loaded,
      ensurePhoneNumbersLoaded,
      refreshPhoneNumbers,
      addPhoneNumber,
      deletePhoneNumber,
      markPhoneNumberVerified
    }}>
      {children}
    </PhoneNumbersContext.Provider>
  );
};

export const usePhoneNumbers = () => {
  const ctx = useContext(PhoneNumbersContext);
  if (!ctx) throw new Error('usePhoneNumbers must be used within a PhoneNumbersProvider');
  return ctx;
};
